
import React, { useState, useEffect } from 'react';
import { Input, Button, Upload, message, Alert, Badge } from 'antd';
import { UploadOutlined, IdcardOutlined, HomeOutlined, FileProtectOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Verifications.css';

const { TextArea } = Input;

const Verifications = () => {
  const [einNumber, setEinNumber] = useState('');
  const [proofOfAddressDoc, setProofOfAddressDoc] = useState(null);
  const [govIssuedIdDoc, setGovIssuedIdDoc] = useState(null);
  const [proofOfAuthorityDoc, setProofOfAuthorityDoc] = useState(null);
  const [extraDocs, setExtraDocs] = useState('');
  const [extraDocsFile, setExtraDocsFile] = useState(null);
  const [status, setStatus] = useState('Pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchVerificationData = async () => {
    setLoading(true);
    try {
      console.log('Envoi de la requête vers /backend/admin_get_verification.php');
      const response = await fetch('/backend/admin_get_verification.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Données parsées:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        if (data.message !== 'Aucune vérification trouvée') {
          setEinNumber(data.ein_number || '');
          setProofOfAddressDoc(data.proof_of_address_path ? { name: data.proof_of_address_path.split('/').pop() } : null);
          setGovIssuedIdDoc(data.gov_issued_id_path ? { name: data.gov_issued_id_path.split('/').pop() } : null);
          setProofOfAuthorityDoc(data.proof_of_authority_path ? { name: data.proof_of_authority_path.split('/').pop() } : null);
          setExtraDocs(data.extra_docs || '');
          setExtraDocsFile(data.extra_docs_path ? { name: data.extra_docs_path.split('/').pop() } : null);
          setStatus(data.status || 'Pending');
        }
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Erreur 403: Session expirée');
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (file, setFile) => {
    setFile(file);
    message.success(`${file.name} selected successfully!`);
    return false; // Prevent default upload
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('ein_number', einNumber);
      formData.append('extra_docs', extraDocs);
      if (proofOfAddressDoc) formData.append('proof_of_address_doc', proofOfAddressDoc);
      if (govIssuedIdDoc) formData.append('gov_issued_id_doc', govIssuedIdDoc);
      if (proofOfAuthorityDoc) formData.append('proof_of_authority_doc', proofOfAuthorityDoc);
      if (extraDocsFile) formData.append('extra_docs_file', extraDocsFile);

      console.log('Envoi des données vers /backend/admin_submit_verification.php');
      const response = await fetch('/backend/admin_submit_verification.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      console.log('Statut HTTP:', response.status);
      const data = await response.json();
      console.log('Réponse:', data);
      if (response.ok) {
        message.success('Vérification soumise avec succès');
        setStatus('Pending');
        setError(null);
        fetchVerificationData(); // Recharger les données
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || 'Erreur lors de la soumission');
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerificationData();
  }, []);

  const getStatusBadge = (status) => {
    let color, text;
    switch (status) {
      case 'Verified':
        color = '#52c41a';
        text = 'VERIFIED';
        break;
      case 'Pending':
        color = '#faad14';
        text = 'PENDING';
        break;
      case 'Rejected':
        color = '#ff4d4f';
        text = 'REJECTED';
        break;
      default:
        color = '#d9d9d9';
        text = 'UNKNOWN';
    }
    return (
      <Badge
        count={text}
        style={{
          backgroundColor: color,
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
        }}
      />
    );
  };

  return (
    <div className="verifications-container">
      <div className="verified-badge">{getStatusBadge(status)}</div>
      <h2 className="section-title">Your Verifications</h2>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}
      <div className="verification-sections">
        <div className="section">
          <h3 className="section-header">
            <IdcardOutlined className="section-icon" /> Business Details
          </h3>
          <div className="form-group">
            <label className="form-label">EIN Number</label>
            <Input
              placeholder="Enter EIN Number"
              value={einNumber}
              onChange={(e) => setEinNumber(e.target.value)}
              className="input-field"
              prefix={<FileTextOutlined />}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Proof Of Address</label>
            <Upload
              beforeUpload={(file) => handleUpload(file, setProofOfAddressDoc)}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} className="upload-btn">
                {proofOfAddressDoc ? proofOfAddressDoc.name : 'Upload Document'}
              </Button>
            </Upload>
          </div>
        </div>
        <div className="section">
          <h3 className="section-header">
            <HomeOutlined className="section-icon" /> Ownership Or Authorized Representative Details
          </h3>
          <div className="form-group">
            <label className="form-label">Government Issued ID</label>
            <Upload
              beforeUpload={(file) => handleUpload(file, setGovIssuedIdDoc)}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} className="upload-btn">
                {govIssuedIdDoc ? govIssuedIdDoc.name : 'Upload Document'}
              </Button>
            </Upload>
          </div>
          <div className="form-group">
            <label className="form-label">Proof Of Authority</label>
            <Upload
              beforeUpload={(file) => handleUpload(file, setProofOfAuthorityDoc)}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} className="upload-btn">
                {proofOfAuthorityDoc ? proofOfAuthorityDoc.name : 'Upload Document'}
              </Button>
            </Upload>
          </div>
        </div>
      </div>
      <div className="extra-docs-section">
        <h3 className="section-header">
          <FileProtectOutlined className="section-icon" /> Extra Documents
        </h3>
        <div className="form-group">
          <label className="form-label">Manual Input/Type</label>
          <TextArea
            placeholder="Enter extra documents details"
            value={extraDocs}
            onChange={(e) => setExtraDocs(e.target.value)}
            className="textarea-field"
            rows={4}
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <Upload
            beforeUpload={(file) => handleUpload(file, setExtraDocsFile)}
            showUploadList={false}
            disabled={loading}
          >
            <Button icon={<UploadOutlined />} className="upload-btn">
              {extraDocsFile ? extraDocsFile.name : 'Upload Extra Document'}
            </Button>
          </Upload>
        </div>
        <Button type="primary" onClick={handleSubmit} className="submit-btn" loading={loading}>
          Submit Verification
        </Button>
      </div>
    </div>
  );
};

export default Verifications;
